import { api } from "@/lib/api-client";
import type {
  CustomerAddress,
  CreateAddressPayload,
  UpdateAddressPayload,
  AddressAutocompleteResult,
  AddressAutocompleteParams,
  ListAddressesParams,
} from "@/types";

import { searchAddress, reverseGeocode, type GeocodeResult } from "@/lib/geocoding";

/**
 * Géocodage Photon, dans la forme que les écrans consomment déjà.
 *
 * Le web appelait `api.mapbox.com` directement depuis le navigateur : un jeton
 * Mapbox dans l'URL à chaque frappe, une facturation à la requête, et des
 * résultats qui ne correspondaient pas à ceux de l'app mobile pour la même
 * recherche. Photon est le moteur de l'app client et de la console — même
 * source de données, mêmes résultats, sans clé.
 */
function toAutocompleteResult(r: GeocodeResult): AddressAutocompleteResult {
  return {
    place_id: r.id ?? `${r.latitude},${r.longitude}`,
    formatted: r.description,
    latitude: r.latitude,
    longitude: r.longitude,
    country: r.country ?? null,
    city: r.city ?? null,
    postcode: r.postcode ?? null,
  };
}

async function photonAutocomplete(
  params: AddressAutocompleteParams,
): Promise<AddressAutocompleteResult[]> {
  const results = await searchAddress(params.query, {
    limit: params.limit ?? 5,
    countryCode: params.country ?? "DZ",
    language: params.language,
    near:
      params.latitude !== undefined && params.longitude !== undefined
        ? { latitude: params.latitude, longitude: params.longitude }
        : undefined,
  });
  return results.map(toAutocompleteResult);
}

/** Coordonnées GPS → adresse lisible, pour « utiliser ma position ». */
async function photonReverse(
  latitude: number,
  longitude: number,
): Promise<AddressAutocompleteResult | null> {
  const r = await reverseGeocode({ latitude, longitude });
  if (!r) return null;
  // On garde les coordonnées demandées, pas celles du point OSM le plus proche :
  // c'est là que la personne se trouve, et c'est ce qui tarifera la livraison.
  return { ...toAutocompleteResult(r), latitude, longitude };
}

export const addressService = {
  photonAutocomplete,
  photonReverse,

  // GET /addresses/autocomplete
  autocomplete: (params: AddressAutocompleteParams, token?: string) => {
    const qs = new URLSearchParams({ query: params.query });
    if (params.provider) qs.set("provider", params.provider);
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.latitude !== undefined) qs.set("latitude", String(params.latitude));
    if (params.longitude !== undefined) qs.set("longitude", String(params.longitude));
    if (params.country) qs.set("country", params.country);
    if (params.language) qs.set("language", params.language);
    return api.get<AddressAutocompleteResult[]>(
      `/addresses/autocomplete?${qs.toString()}`,
      { token }
    );
  },

  // POST /addresses
  create: (payload: CreateAddressPayload, token?: string) =>
    api.post<CustomerAddress>("/addresses", payload, { token }),

  // GET /addresses
  list: (params: ListAddressesParams = {}, token?: string) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.limit) qs.set("limit", String(params.limit));
    if (params.search) qs.set("search", params.search);
    if (params.sortBy) qs.set("sortBy", params.sortBy);
    if (params.sortOrder) qs.set("sortOrder", params.sortOrder);
    if (params.type) qs.set("type", params.type);
    if (params.is_default !== undefined) qs.set("is_default", String(params.is_default));
    if (params.is_favorite !== undefined) qs.set("is_favorite", String(params.is_favorite));
    const query = qs.toString();
    return api.get<CustomerAddress[]>(`/addresses${query ? `?${query}` : ""}`, { token });
  },

  // GET /addresses/favorites
  listFavorites: (token?: string) =>
    api.get<CustomerAddress[]>("/addresses/favorites", { token }),

  // GET /addresses/:ulid
  getById: (ulid: string, token?: string) =>
    api.get<CustomerAddress>(`/addresses/${ulid}`, { token }),

  // PATCH /addresses/:ulid
  update: (ulid: string, payload: UpdateAddressPayload, token?: string) =>
    api.patch<CustomerAddress>(`/addresses/${ulid}`, payload, { token }),

  // DELETE /addresses/:ulid
  delete: (ulid: string, token?: string) =>
    api.delete<void>(`/addresses/${ulid}`, { token }),

  // POST /addresses/:ulid/favorite
  addFavorite: (ulid: string, token?: string) =>
    api.post<void>(`/addresses/${ulid}/favorite`, undefined, { token }),

  // DELETE /addresses/:ulid/favorite
  removeFavorite: (ulid: string, token?: string) =>
    api.delete<void>(`/addresses/${ulid}/favorite`, { token }),
};
